import React, { useMemo, useState } from 'react';
import {
  Button,
  DropDown,
  SegmentedControl,
  SidePanel,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import { MerchandisingRuleTypes } from 'src/services';
import {
  buildConditionValues,
  deriveConditionRawValue,
  getRuleSummaryText,
  needsConditionValueInput,
} from './helpers';
import { ConditionFieldOption, MerchRuleRow, RuleEditDraft } from './types';
import {
  ACTION_OPTIONS,
  INTENSITY_OPTIONS,
  getIntensityLevelFromWeight,
  INTENSITY_WEIGHT_BY_LEVEL,
  PromoteIntensityLevel,
} from './constants';
import { useConditionField } from './useConditionField';
import { ConditionValueInput } from './ConditionValueInput';

const { RuleAction } = MerchandisingRuleTypes;

export interface RuleEditDrawerProps {
  row: MerchRuleRow;
  fieldOptions: ConditionFieldOption[];
  onClose: () => void;
  onSave: (row: MerchRuleRow, draft: RuleEditDraft) => Promise<void>;
  onResetToControl: (row: MerchRuleRow) => Promise<void>;
}

export const RuleEditDrawer = ({
  row,
  fieldOptions,
  onClose,
  onSave,
  onResetToControl,
}: RuleEditDrawerProps): JSX.Element => {
  const current = row.test ?? row.control;
  const currentSubRule = current.subRules?.[0];

  const [action, setAction] = useState<MerchandisingRuleTypes.RuleAction>(current.action);
  const [intensity, setIntensity] = useState<PromoteIntensityLevel>(
    getIntensityLevelFromWeight(current.weight)
  );
  const [isSaving, setIsSaving] = useState(false);

  const {
    field,
    subType,
    value,
    selectedField,
    category,
    subTypeOptions,
    setValue,
    setSubType,
    handleFieldChange,
    reset: resetConditionField,
  } = useConditionField(fieldOptions, {
    field: currentSubRule?.field ?? '',
    subType: currentSubRule?.subType ?? '',
    value: deriveConditionRawValue(currentSubRule?.subType, currentSubRule?.values),
  });

  const isWeightedAction = action === RuleAction.Promote || action === RuleAction.Demote;

  const previewText = useMemo(() => {
    if (!field || !subType) return undefined;
    const conditionLabel = subTypeOptions.find(option => option.value === subType)?.text ?? '';
    const valueText = needsConditionValueInput(subType) ? ` ${value || '…'}` : '';
    const actionText = ACTION_OPTIONS.find(option => option.value === action)?.text ?? '';
    const intensityText = isWeightedAction ? ` (${intensity.toLowerCase()})` : '';
    return `Variant B runs ${actionText}${intensityText} · ${field} ${conditionLabel}${valueText}. Counts as one change.`;
  }, [field, subType, subTypeOptions, value, action, isWeightedAction, intensity]);

  // A rule created outside this bounded editor can carry a subtype (HasValue, IsBetween, etc.)
  // that never appears in `subTypeOptions` - saving it anyway would fall through
  // `buildConditionValues`'s default case and silently wipe the condition's values.
  const isSubTypeSupported = subTypeOptions.some(option => option.value === subType);

  const isSaveDisabled =
    !field ||
    !subType ||
    !isSubTypeSupported ||
    (needsConditionValueInput(subType as MerchandisingRuleTypes.MerchandisingRuleSubType) &&
      value.trim() === '');

  const handleSave = (): void => {
    if (!selectedField || !subType || !isSubTypeSupported) return;

    setIsSaving(true);
    onSave(row, {
      action,
      weight: isWeightedAction ? INTENSITY_WEIGHT_BY_LEVEL[intensity] : 0,
      field,
      fieldType: selectedField.fieldType,
      subType,
      values: buildConditionValues(subType, category, value),
    })
      .then(onClose)
      .finally(() => setIsSaving(false));
  };

  // Reset writes the control's values back to the Test rule, but doesn't close the drawer - sync
  // the dropdowns/value to match what's now on the Test rule instead of leaving stale selections.
  const handleReset = (): void => {
    setIsSaving(true);
    onResetToControl(row)
      .then(() => {
        const controlSubRule = row.control.subRules?.[0];
        setAction(row.control.action);
        setIntensity(getIntensityLevelFromWeight(row.control.weight));
        resetConditionField(
          controlSubRule?.field ?? '',
          controlSubRule?.subType ?? '',
          deriveConditionRawValue(controlSubRule?.subType, controlSubRule?.values)
        );
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <SidePanel className='syte-rule-edit-panel' opened onClose={onClose}>
      <div className='syte-rule-edit-drawer-header'>
        <Typography
          type={TypographyType.Body}
          variant={TypographyVariant.SmallRegular}
          className='syte-rule-edit-drawer-label'
        >
          EDIT FOR VARIANT B
        </Typography>
        <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
          {row.control.name}
        </Typography>
      </div>
      <div className='syte-rule-edit-drawer-body'>
        <Typography
          type={TypographyType.Body}
          variant={TypographyVariant.SmallRegular}
          className='syte-rule-edit-drawer-label'
        >
          IN CONTROL A
        </Typography>
        <div className='syte-rule-edit-drawer-control-box'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
            {getRuleSummaryText(row.control)}
          </Typography>
        </div>
        <Typography
          type={TypographyType.Body}
          variant={TypographyVariant.SmallRegular}
          className='syte-rule-edit-drawer-caption'
        >
          Control A and Production keep running this rule exactly as it is today. Nothing you change
          here reaches your live shop.
        </Typography>

        <Typography
          type={TypographyType.Body}
          variant={TypographyVariant.SmallRegular}
          className='syte-rule-edit-drawer-label'
        >
          IN VARIANT B
        </Typography>
        <div className='syte-rule-edit-drawer-condition-row'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
            I want to
          </Typography>
          <DropDown
            options={ACTION_OPTIONS}
            value={action}
            onChange={val => setAction(val as MerchandisingRuleTypes.RuleAction)}
            className='syte-rule-edit-drawer-action'
          />
          {isWeightedAction && (
            <SegmentedControl<PromoteIntensityLevel>
              options={INTENSITY_OPTIONS}
              value={intensity}
              onChange={setIntensity}
            />
          )}
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
            where
          </Typography>
          <DropDown
            placeholder='Data field'
            options={fieldOptions}
            value={field || undefined}
            onChange={val => handleFieldChange(String(val))}
            className='syte-rule-edit-drawer-field'
          />
          <DropDown
            placeholder='Condition'
            options={subTypeOptions}
            value={subType || undefined}
            disabled={!field}
            onChange={val => {
              setSubType(val as MerchandisingRuleTypes.MerchandisingRuleSubType);
              setValue('');
            }}
            className='syte-rule-edit-drawer-condition'
          />
        </div>

        <ConditionValueInput
          subType={subType}
          category={category}
          value={value}
          onChange={setValue}
          className='syte-rule-edit-drawer-value'
        />

        {previewText && (
          <div className='syte-rule-edit-drawer-preview'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              {previewText}
            </Typography>
          </div>
        )}
      </div>
      <div className='syte-rule-edit-drawer-footer'>
        <Button variant='primary' onClick={handleSave} disabled={isSaving || isSaveDisabled}>
          Save for Variant B
        </Button>
        <Button variant='secondary' onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant='tertiary'
          className='syte-rule-edit-drawer-reset'
          onClick={handleReset}
          disabled={isSaving}
        >
          Reset to control
        </Button>
      </div>
    </SidePanel>
  );
};
